import { Box } from "@chakra-ui/react";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { ClickableLinkPlugin } from "@lexical/react/LexicalClickableLinkPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { SelectionAlwaysOnDisplay } from "@lexical/react/LexicalSelectionAlwaysOnDisplay";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { useLexicalEditable } from "@lexical/react/useLexicalEditable";
import { EditorState, LexicalEditor } from "lexical";
import { useEffect, useState } from "react";
import { ContentEditable } from "../components";
import { useSettings } from "../context/SettingsContext";
import { useSharedHistoryContext } from "../context/SharedHistoryContext";
import { EditorProps } from "../Editor";
import AutoLinkPlugin from "../plugins/AutoLinkPlugin";
import ContextMenuPlugin from "../plugins/ContextMenuPlugin";
import FloatingLinkEditorPlugin from "../plugins/FloatingLinkEditorPlugin";
import ImageDragDropPaste from "../plugins/ImageDragDropPastePlugin";
import ImagesPlugin from "../plugins/ImagesPlugin";
import LinkPlugin from "../plugins/LinkPlugin";
import ListMaxIndentLevelPlugin from "../plugins/ListMaxIndentLevelPlugin";
import MarkdownShortcutPlugin from "../plugins/MarkdownShortcutPlugin";
import { MaxLengthPlugin } from "../plugins/MaxLengthPlugin";
import ShortcutsPlugin from "../plugins/ShortcutsPlugin";
import ToolbarPlugin from "../plugins/ToolbarPlugin";
import VariablesPlugin from "../plugins/VariablesPlugin";
import { CAN_USE_DOM } from "../utils/canUseDOM";

export const FullVersionEditor = ({
  name,
  id,
  width,
  height,
  maxLength,
  placeholder,
  editorRef,
  onChange,
  onBlur,
}: EditorProps): JSX.Element => {
  const { settings } = useSettings();
  const { hasLinkAttributes, hideToolbar } = settings;
  const { historyState } = useSharedHistoryContext();

  const isEditable = useLexicalEditable();
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] =
    useState<boolean>(false);
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    if (editorRef) {
      editorRef(activeEditor);
    }
  }, [activeEditor, editorRef]);

  const handleOnChange = (
    editorState: EditorState,
    editor: LexicalEditor,
    tags: Set<string>
  ) => {
    onChange?.(editorState);
  };

  const handleOnBlur = (e: React.FocusEvent<any>) => {
    onBlur?.(editor.getEditorState());
  };

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport =
        CAN_USE_DOM && window.matchMedia("(max-width: 1025px)").matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener("resize", updateViewPortWidth);

    return () => {
      window.removeEventListener("resize", updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  return (
    <>
      {!hideToolbar ? (
        <ToolbarPlugin
          editor={editor}
          activeEditor={activeEditor}
          setActiveEditor={setActiveEditor}
        />
      ) : null}
      <ShortcutsPlugin editor={activeEditor} />
      <Box ref={onRef}>
        {maxLength && <MaxLengthPlugin maxLength={maxLength} />}
        {settings.enableImage ? <ImageDragDropPaste /> : null}
        {settings.enableAutofocus ? <AutoFocusPlugin /> : null}
        <SelectionAlwaysOnDisplay />
        <ClearEditorPlugin />
        {settings.enableVariables && <VariablesPlugin />}
        <HistoryPlugin externalHistoryState={historyState} />
        <RichTextPlugin
          contentEditable={
            <Box className="editor-scroller">
              <Box
                className="editor"
                overflow="auto"
                width={width}
                height={height}
              >
                <ContentEditable
                  name={name}
                  id={id}
                  placeholder={placeholder || ""}
                  onBlur={handleOnBlur}
                />
              </Box>
            </Box>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <MarkdownShortcutPlugin />
        <ListPlugin />
        <CheckListPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <ImagesPlugin />
        <AutoLinkPlugin />
        <LinkPlugin hasLinkAttributes={hasLinkAttributes} />
        <ClickableLinkPlugin disabled={isEditable} />
        <HorizontalRulePlugin />
        <TabIndentationPlugin />
        <ContextMenuPlugin />
        {onChange && (
          <OnChangePlugin
            onChange={handleOnChange}
            ignoreSelectionChange={true}
          />
        )}
        {floatingAnchorElem && (
          <>
            <FloatingLinkEditorPlugin anchorElem={floatingAnchorElem} />
          </>
        )}
      </Box>
    </>
  );
};
